import {useParams} from 'react-router-dom'
import {AlertTriangle, Plus} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {Stack} from '@/components/shared/Stack'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {Button} from '@/components/ui/button'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {Badge} from '@/components/ui/badge'
import {RehirePolicy, InboundConnectorType} from '@/types/inbound-connector'
import {
    useInboundConnector,
    useRestConfig,
    useScriptInboundConfig,
    useUpdateConnectorSettings,
} from '@/hooks/use-inbound-connectors'
import {InboundAttributeMappingCard} from '@/features/inbound-connectors/components/InboundAttributeMappingEditor'
import {InlineSelectField} from '@/features/inbound-connectors/components/InlineSelectField'

export function ReconciliationSubPage() {
    const i18n = useI18n()
    const {id} = useParams<{id: string}>()
    const {hasPermission} = useAuth()
    const canManage = hasPermission(permissions.inboundConnectors.manage)

    const {data: connector, isLoading: connectorLoading} = useInboundConnector(id)
    const isScriptConnector = connector?.connectorType === InboundConnectorType.SCRIPT
    const {data: restConfig, isLoading: restLoading} = useRestConfig(
        !isScriptConnector && connector ? connector.id : undefined
    )
    const {data: scriptConfig, isLoading: scriptLoading} = useScriptInboundConfig(
        isScriptConnector && connector ? connector.id : undefined
    )

    const updateMutation = useUpdateConnectorSettings(connector?.id ?? '', {silent: true})

    if (connectorLoading || (isScriptConnector ? scriptLoading : restLoading)) {
        return (
            <Card data-testid="reconciliation-subpage-skeleton">
                <CardContent>
                    <MutedText>{i18n.common.loading}</MutedText>
                </CardContent>
            </Card>
        )
    }

    if (!connector) return null

    const settings = connector.settings
    const policy = settings.rehirePolicy
    const showDestructiveWarning = policy === RehirePolicy.IGNORE
        && connector.targetSystemAuthoritativeForIdentities

    const policyLabel = (p: RehirePolicy): string => {
        switch (p) {
            case RehirePolicy.AUTO_RESURRECT: return i18n.inboundConnectors.rehirePolicyAutoResurrect
            case RehirePolicy.IGNORE: return i18n.inboundConnectors.rehirePolicyIgnore
            case RehirePolicy.HUMAN_REVIEW:
            default: return i18n.inboundConnectors.rehirePolicyHumanReview
        }
    }

    const handleRehireChange = async (next: string): Promise<void> => {
        await updateMutation.mutateAsync({
            safeguards: settings.safeguards,
            validation: settings.validation,
            batchSize: settings.batchSize,
            entityOperations: settings.entityOperations,
            rehirePolicy: next as RehirePolicy,
            version: connector.version,
        })
    }

    const mappings = (isScriptConnector ? scriptConfig?.attributeMappings : restConfig?.attributeMappings) ?? null
    const hasMappings = mappings && mappings.length > 0

    return (
        <Stack gap="lg" data-testid="reconciliation-subpage">
            <Card data-testid="rehire-policy-section" >
                <RowCardHeader>
                    <div>
                        <CardTitle data-testid="card-title-rehire-policy">
                            {i18n.inboundConnectors.rehirePolicySection}
                        </CardTitle>
                        <CardDescription>{i18n.inboundConnectors.rehirePolicyHelp}</CardDescription>
                    </div>
                </RowCardHeader>
                <CardContent>
                    <Stack gap="md">
                        <DetailGrid columns={2}>
                            <InlineSelectField
                                label={i18n.inboundConnectors.rehirePolicyLabel}
                                value={policy}
                                canEdit={canManage}
                                readOnlyValue={
                                    <Badge variant="outline" data-testid="badge-rehire-policy">
                                        {policyLabel(policy)}
                                    </Badge>
                                }
                                options={[
                                    /* eslint-disable i18next/no-literal-string -- testid string literals */
                                    {value: RehirePolicy.HUMAN_REVIEW, label: i18n.inboundConnectors.rehirePolicyHumanReview, testId: 'option-rehire-policy-human-review'},
                                    {value: RehirePolicy.AUTO_RESURRECT, label: i18n.inboundConnectors.rehirePolicyAutoResurrect, testId: 'option-rehire-policy-auto-resurrect'},
                                    {value: RehirePolicy.IGNORE, label: i18n.inboundConnectors.rehirePolicyIgnore, testId: 'option-rehire-policy-ignore'},
                                    /* eslint-enable i18next/no-literal-string */
                                ]}
                                onChange={handleRehireChange}
                                testId="select-rehire-policy"
                                aria-label={i18n.inboundConnectors.editRehirePolicyInline}
                                fullWidth
                            />
                        </DetailGrid>
                        {showDestructiveWarning && (
                            <InfoBanner
                                variant="destructive"
                                icon={AlertTriangle}
                                data-testid="banner-rehire-policy-ignore-warning"
                            >
                                {i18n.inboundConnectors.rehirePolicyIgnoreAuthoritativeWarning}
                            </InfoBanner>
                        )}
                        <DetailField
                            label=""
                            value={<MutedText>{i18n.inboundConnectors.rehirePolicyHelper}</MutedText>}
                            fullWidth
                        />
                    </Stack>
                </CardContent>
            </Card>

            {hasMappings && (
                <InboundAttributeMappingCard
                    connectorId={connector.id}
                    attributeMappings={mappings}
                    canUpdate={canManage}
                    partyTypeCode={settings.partyTypeCode}
                    identityTypeCode={settings.identityTypeCode}
                    createParty={settings.createParty}
                    createIdentity={settings.createIdentity}
                    createAccount={settings.createAccount}
                    externalIdField={settings.externalIdField}
                    lifecycleSourceField={settings.lifecycleSourceField}
                    lifecycleEnabledValue={settings.lifecycleEnabledValue}
                    connectorVersion={connector.version}
                    currentSettings={settings}
                />
            )}

            {!hasMappings && (
                <Card data-testid="reconciliation-empty-state">
                    <RowCardHeader>
                        <div>
                            <CardTitle data-testid="card-title-attribute-mappings-empty">
                                {i18n.inboundConnectors.attributeMappingsSection}
                            </CardTitle>
                            <CardDescription>
                                {i18n.inboundConnectors.attributeMappingsHelp}
                            </CardDescription>
                        </div>
                    </RowCardHeader>
                    <CardContent>
                        <Stack gap="md">
                            <MutedText>{i18n.inboundConnectors.correlationRulesNotConfigured}</MutedText>
                            {canManage && (
                                <InlineRow>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        data-testid="btn-add-correlation-rule"
                                    >
                                        <Icon icon={Plus} className="mr-2"/>
                                        {i18n.inboundConnectors.addCorrelationRule}
                                    </Button>
                                </InlineRow>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Stack>
    )
}
